// Locale resolution and switching.
//
// Split out of i18n.js because these are the only functions that read session
// data. Keeping them here lets i18n.js (and the @/utils/i18n barrel that
// re-exports it) stay a leaf, so modules that only need i18n_t do not inherit
// the session layer.
//
// Precedence for the active locale is: ?locale= override for Pendozers, then the
// user's locale, then the subscription's, then the browser's, then 'en'.
import { normalizeLocale } from '@pendo/i18n';
import { getFileFromCDN } from '@/utils/get-from-cdn.utils';
import i18nDateTimeFormatter from './i18n-datetime-formatter';
import i18nListFormatter from './i18n-list-formatter';
import { getActiveUser, getActiveSubscription } from '@/data/session/session-globals';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, UNANNOUNCED_LOCALES } from '@/../locale.constants.mjs';
import { i18n, loadLocaleMessages } from './i18n';
import momentTimeZone from 'moment-timezone';
import { scheduleApplyTitle } from '@/vue-router/title-context.js';

function getBrowserLocale () {
    const regionalLanguage = window.navigator.language;

    if (!regionalLanguage) {
        return false;
    }

    // Only Pendozers should access unannounced non-English locales
    if (!getActiveUser()?.isPendozer && UNANNOUNCED_LOCALES.includes(regionalLanguage)) {
        return 'en';
    }

    const noVariantLocale = regionalLanguage.split('-')[0];

    // we do not care about regional variants of en, es, ja, or de (for now)
    return ['en', 'es', 'ja', 'de'].includes(noVariantLocale) ? noVariantLocale : regionalLanguage;
}

function getLocaleOverride () {
    if (!getActiveUser()?.isPendozer) {
        return null;
    }

    const params = new URLSearchParams(window.location.search);

    return params.get('locale');
}

const REMEMBERED_LOCALE_KEY = 'pendo-remembered-locale';

function rememberLocale (locale) {
    try {
        localStorage.setItem(REMEMBERED_LOCALE_KEY, locale);
    } catch {
        // Storage can be disabled; the next boot just skips the preload.
    }
}

function getRememberedLocale () {
    try {
        return localStorage.getItem(REMEMBERED_LOCALE_KEY);
    } catch {
        return null;
    }
}

// Starts the likely locale download before /api/init; refreshLocale replaces it if wrong.
function preloadRememberedLocale () {
    const locale = getRememberedLocale();

    if (!SUPPORTED_LOCALES.includes(locale)) {
        return Promise.resolve();
    }

    return setLocale(locale).catch(() => {});
}

function refreshLocale () {
    const override = getLocaleOverride();
    const user = getActiveUser();
    const locale = override || user?.locale || getActiveSubscription()?.locale || getBrowserLocale() || 'en';

    if (user && !override) {
        rememberLocale(locale);
    }

    return setLocale(locale);
}

// Literal imports so each locale is its own chunk; en is built into moment.
const momentLocaleLoaders = {
    ja: () => import('moment/locale/ja'),
    de: () => import('moment/locale/de'),
    'pt-BR': () => import('moment/locale/pt-br'),
    es: () => import('moment/locale/es'),
};

let latestLocaleRequest = 0;
let appliedMomentLocale = momentTimeZone.locale();
let pendingLocale = Promise.resolve();

async function applyLocale (locale) {
    if (!locale || locale === i18n.locale) {
        return;
    }

    const request = ++latestLocaleRequest;
    const originalLocale = locale;
    locale = normalizeLocale(locale);
    // Untranslated locales show English text but keep their own date and list formats.
    const translated = SUPPORTED_LOCALES.includes(locale) || UNANNOUNCED_LOCALES.includes(locale);
    let messagesLocale = translated ? originalLocale : DEFAULT_LOCALE;

    // i18n.changeLocale does not return its load promise, so download the messages here to await them.
    const [, msgs] = await Promise.all([
        // A failed chunk keeps the previous moment locale rather than blocking the switch.
        momentLocaleLoaders[locale]?.().catch(() => {}),
        // No English is bundled, so retry or fall back to en rather than show raw keys.
        loadLocaleMessages(messagesLocale).catch(() => {
            messagesLocale = DEFAULT_LOCALE;

            return loadLocaleMessages(DEFAULT_LOCALE);
        }),
    ]);

    if (request !== latestLocaleRequest) {
        // A late locale chunk re-registers itself as moment's global locale.
        momentTimeZone.locale(appliedMomentLocale);

        return;
    }

    i18n.changeLocale({ locale: messagesLocale, msgs });
    appliedMomentLocale = momentTimeZone.locale(locale);
    i18nDateTimeFormatter.locale = locale;
    i18nListFormatter.locale = locale;
    // Route titles are plain strings, so re-apply the current one in the new locale.
    scheduleApplyTitle();
}

function setLocale (locale) {
    const applied = applyLocale(locale);
    pendingLocale = applied.catch(() => {});

    return applied;
}

// Never rejects; also waits for locale requests made while waiting.
function waitForLocale () {
    const pending = pendingLocale;

    return pending.then(() => (pending === pendingLocale ? undefined : waitForLocale()));
}

async function loadLocaleOptions () {
    // https://pendo-io.atlassian.net/browse/APP-79945
    // Locale file is not fully functional, so we need to manually add supported locales
    const locales = await getFileFromCDN('/i18n/locales.json');

    // Make sure all locales in SUPPORTED_LOCALES are included
    SUPPORTED_LOCALES.forEach((supportedLocale) => {
        if (!locales.some((locale) => locale.id === supportedLocale)) {
            switch (supportedLocale) {
                case 'en':
                    locales.push({ id: supportedLocale, displayName: 'English' });
                    break;
                case 'ja':
                    locales.push({ id: supportedLocale, displayName: '日本語' });
                    break;
                case 'de':
                    locales.push({ id: supportedLocale, displayName: 'Deutsch' });
                    break;
                case 'pt-BR':
                    locales.push({ id: supportedLocale, displayName: 'Português' });
                    break;
                case 'es':
                    locales.push({ id: supportedLocale, displayName: 'Español' });
                    break;
            }
        }
    });

    const localeOptions = locales.filter((locale) => SUPPORTED_LOCALES.includes(locale.id));

    // This is temporary until we officially support/announce these locales
    if (getActiveUser()?.isPendozer) {
        const unannouncedLocaleDisplayNames = {
            'fr-FR': 'Français',
        };

        UNANNOUNCED_LOCALES.forEach((localeId) => {
            localeOptions.push({
                id: localeId,
                displayName: unannouncedLocaleDisplayNames[localeId],
            });
        });
    }

    return localeOptions;
}

export { setLocale, refreshLocale, loadLocaleOptions, rememberLocale, preloadRememberedLocale, waitForLocale };
